import { Dialog } from '@headlessui/react';
import { X, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FeeInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FeeInfoDialog({ isOpen, onClose }: FeeInfoDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[70]">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="rounded-full bg-blue-100 p-2">
                <Info className="h-5 w-5 text-blue-600" />
              </div>
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {t('donation.fees.title')}
              </Dialog.Title>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('donation.fees.description')}
            </p>

            {/* Fee Structure */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h3 className="font-medium text-sm text-gray-900">
                {t('donation.fees.structure')}
              </h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('donation.fees.cardFee')}</span>
                  <span className="font-medium text-gray-900">7%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('donation.fees.achSepaFee')}</span>
                  <span className="font-medium text-gray-900">5%</span>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  {t('donation.fees.feeNote')}
                </p>
              </div>
            </div>

            {/* Fee Breakdown */}
            <div className="bg-blue-50 rounded-lg p-4 space-y-3">
              <h3 className="font-medium text-sm text-gray-900">
                {t('donation.fees.breakdown')}
              </h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="text-gray-900 font-medium">{t('donation.fees.stripeFees')}</div>
                    <div className="text-xs text-gray-600 mt-0.5">{t('donation.fees.stripeFeesDesc')}</div>
                  </div>
                  <span className="text-gray-600 ml-2 whitespace-nowrap">~2.9% + $0.30</span>
                </div>

                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="text-gray-900 font-medium">{t('donation.fees.platformFees')}</div>
                    <div className="text-xs text-gray-600 mt-0.5">{t('donation.fees.platformFeesDesc')}</div>
                  </div>
                  <span className="text-gray-600 ml-2 whitespace-nowrap">~2-4%</span>
                </div>
              </div>
            </div>

            {/* Example Calculation */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h3 className="font-medium text-sm text-gray-900">
                {t('donation.fees.example')}
              </h3>

              {/* ACH/SEPA Example */}
              <div className="space-y-2">
                <div className="text-xs space-y-1 text-gray-600 bg-gray-50 p-3 rounded">
                  <div className="flex justify-between">
                    <span>{t('donation.fees.donationAmount')}</span>
                    <span className="font-mono">$25.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('donation.fees.platformFee')} (5%)</span>
                    <span className="font-mono">+ $1.25</span>
                  </div>
                  <div className="flex justify-between pt-1 border-t border-gray-200 font-medium text-gray-900">
                    <span>{t('donation.fees.totalCharged')}</span>
                    <span className="font-mono">$26.25</span>
                  </div>
                  <div className="pt-2 text-xs text-gray-500">
                    {t('donation.fees.creatorReceives', { amount: '$25.00' })}
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-2">
              <p className="text-xs text-gray-500">
                {t('donation.fees.disclaimer')}
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t('donation.fees.close')}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

